<template>
  <div
    class="navigation-controller"
  >
    <owal-add-face
      v-if="showAddUser"
      @close="handleClose"
    />
    <owal-search-bar
      class="bar"
      :items="this.faces"
      :callback="this.setFilteredFaces"
      :filters="this.filters"
      :search-keys="this.searchKeys"
      :sorts="this.sorts"
      :synonyms="this.synonyms"
    >
      <div
        v-if="canPerformOperationForCurrentService('add')"
        slot="trailing-controls"
        class="owal button"
        @click="showAddUser = true"
      >
        <font-awesome-icon
          icon="plus"
          style="color: #475669"
        />
      </div>
    </owal-search-bar>
    <div
      class="scrollview"
    >
      <transition
        name="slide-down-fade"
      >
        <div
          v-if="refreshing"
          class="owal message banner"
        >
          Refreshing Data...
        </div>
      </transition>
      <div
        v-if="filteredFaces && filteredFaces.length"
      >
        <virtual-list
          v-if="isAuthenticated"
          class="list"
          :size="cellHeight"
          :remain="numberOfCells"
          :bench="10"
        >
          <faces-list-cell
            v-for="(face, index) in filteredFaces"
            :key="face.frontend_face_uuid"
            :index="index"
            :active="faceIsActive(face)"
            :face="face"
            :click="function (faceId) {
              this.$router.push(`/faces/${faceId}`)
            }"
          />
        </virtual-list>
      </div>
      <div v-else>
        <div v-if="isAuthenticated">
          <div class="message">
            <h3>No Results</h3>
            <p><font-awesome-icon icon="question-circle" @click='tip = !tip' />
            Try searching for a name.
              <br />
            You can also try things like "role:staff" or "room:G06" </p>
            <p v-if="tip">
              Things you can search for:
              <ul style="text-align: left;">
                <li v-for='(key, index) in searchKeys' :key='index'>
                  {{key}}
                </li>
              </ul>
            </p>
          </div>
        </div>
        <div
          v-else
        >
          <div class="message">
            <h3>No Results</h3>
            <p>You are not logged in.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import OwalAddFace from './AddFace'
import { mapActions, mapGetters } from 'vuex'
import FacesListCell from './FacesListCell'
import moment from 'moment'
import { OwalSearchBar, OwalFilterPredicate, OwalSortPredicate, OwalSearchSynonym, OwalSearchContext } from '@/libraries/owal-ui'

import { AutoSizingList } from '@/mixins'
import VirtualList from 'vue-virtual-scroll-list'
import { titleCase } from '@/libraries/util/string'

export default {
  name: 'Faces',
  components: {
    OwalAddFace,
    FacesListCell,
    VirtualList,
    OwalSearchBar,
  },
  mixins: [AutoSizingList],
  data () {
    return {
      searchKeys: [
        'first_name',
        'last_name',
        'person_name',
        'role',
        'complexion',
        'elopement_risk',
        'last_seen_camera_description',
        'room',
        'bed',
      ],
      scheduledPromise: undefined,
      filterText: '',
      updateHandle: undefined,
      showAddUser: false,
      tip: false,
      sorts: [
        new OwalSortPredicate({
          displayName: 'Seen',
          activeDefault: true,
          comparisonFunction: function (faceA, faceB) {
            const timestampA = faceA.last_seen_datetime
            const timestampB = faceB.last_seen_datetime

            if (!timestampA && !timestampB) {
              return 0
            }

            if (!timestampB) {
              return -1
            }

            if (!timestampA) {
              return 1
            }

            return moment(timestampB).unix() - moment(timestampA).unix()
          } }),
        new OwalSortPredicate({
          displayName: 'Last Name',
          display: true,
          comparisonFunction: function (faceA, faceB) {
            const nameA = faceA.last_name.toUpperCase()
            const nameB = faceB.last_name.toUpperCase()

            if (!nameA && !nameB) {
              return 0
            }

            if (nameA < nameB) {
              return -1
            }

            if (nameA > nameB) {
              return 1
            }

            return 0
          } }),
        new OwalSortPredicate({
          displayName: 'First Name',
          display: true,
          comparisonFunction: function (faceA, faceB) {
            const nameA = faceA.first_name.toUpperCase()
            const nameB = faceB.first_name.toUpperCase()

            if (!nameA && !nameB) {
              return 0
            }

            if (nameA < nameB) {
              return -1
            }

            if (nameA > nameB) {
              return 1
            }

            return nameA > nameB
          } }),
      ],
      filters: [
        new OwalFilterPredicate({ displayName: 'People',
          callback: function (object) {
            return !object.card
          } }),
        new OwalFilterPredicate({ displayName: 'Badges',
          callback: function (object) {
            return object.card
          } }),
      ],
      synonyms: [
        new OwalSearchSynonym({
          rootWord: 'black',
          synonyms: [
            'african american',
          ],
          context: new OwalSearchContext({
            objectKeys: ['complexion'],
          }),
        }),
        new OwalSearchSynonym({
          rootWord: 'caucasian',
          synonyms: [
            'white',
          ],
          context: new OwalSearchContext({
            objectKeys: ['complexion'],
          }),
        }),
        new OwalSearchSynonym({
          rootWord: 'employee',
          synonyms: [
            'staff',
          ],
          context: new OwalSearchContext({
            objectKeys: ['complexion'],
          }),
        }),
      ],
      filteredFaces: [],
    }
  },
  created () {
    let updateLastSeen = this.updateLastSeen
    this.scheduledPromise = new Promise((resolve, reject) => {
      this.updateHandle = setInterval(updateLastSeen, 30 * 1000)
      resolve()
    })
  },
  computed: {
    ...mapGetters({
      faces: 'frontendFaces',
      isAuthenticated: 'isAuthenticated',
      refreshing: 'refreshing',
      canPerformOperationForCurrentService: 'entitlements/canPerformOperationForCurrentService',
    }),
  },
  methods: {
    ...mapActions(['loadFrontendFaces', 'updateLastSeen']),
    titleCase,
    setFilteredFaces (results) {
      console.log('setFilteredFaces performing entitlements check')
      if (!this.canPerformOperationForCurrentService('find_all')) {
        const filteredByRole = results.filter(result => {
          return result.role !== 'employee'
        })
        this.filteredFaces = filteredByRole
      } else this.filteredFaces = results
    },
    faceIsActive (face) {
      if (!this.$route) {
        // console.log(`Faces.vue: Can't tell if face is selected. No access to route.`)
        return false
      }

      if (!this.$route.params) {
        // console.log(`Faces.vue: Can't tell if face is selected. Can't read route params.`)
        return false
      }

      if (!this.$route.params.id) {
        // console.log(`Faces.vue: Can't tell if face is selected. Can't read uuid from route params: ${JSON.stringify(this.$route.params)}`)
        return false
      }

      const activeUUID = this.$route.params.id
      const active = activeUUID === face.frontend_face_uuid

      return active
    },
    showModal () {
      this.showUser = true
    },
    handleClose: function (payload) {
      const action = payload.action
      const success = !!payload.success
      const added = action === 'add' && success
      const uuid = payload.uuid

      this.showAddUser = false
      if (added) {
        this.loadFrontendFaces().then(function () {
          if (uuid) {
            const route = {
              path: `/faces/${uuid}`,
            }
            this.$router.push(route)
          }
        }.bind(this))
      }
    },
  },
}

</script>

<style scoped>

.navigation-controller {
  height: 100%;
  overflow: hidden;
  border-right: 1px solid #475669;
  background: #EFF2F7;
}

.bar {
  z-index: 500;
  width: 100%;
  /* height: 60px; */
  border-right: 1px solid #475669;
}

.owal.button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  border-radius: 4px;
  width: 44px;
  text-align: center;
  margin: 0 5px;
  background: #D3DCE6;
  color: #475669;
}

.owal.button:hover {
  color: #D3DCE6;
  background: #475669;
}

.scrollview {
  padding: 0;
  height: calc(100% - 88px);
}

#search {
  height: 36px;
  display: flex;
  flex-grow: 99;
  flex-direction: row;
  background: #E5E9F2 !important;
  border-radius: 4px;
  border: 1px solid #D3DCE6;
  align-items: center;
  justify-content: middle;
  padding: 10px;
  pointer-events: none;
}

#search input {
  background: #E5E9F2 !important;
  border: none;
  margin: 0;
  flex-grow: 2;
  box-sizing: border-box;
  pointer-events: auto;
  color: #324057;
}

#search input:focus {
  outline: none;
}

.list {
  width: 100%;
}

@media only screen and (max-width: 768px) {
  .scrollview {
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
  }
}

.owal.message.banner, .message h3, .message > p {
  margin: 0.5rem;
  padding: 0.5rem;
  font-family: 'Lato';
  text-align: center;
  background-color: rgba(71, 86, 105, 0.1);
  border-radius: 8px;
}

/* Banner slide in animation. */
.slide-down-fade-enter-active {
  animation: slide-down-fade .3s ease;
}
.slide-down-fade-leave-active {
  animation: slide-down-fade .3s reverse ease;
}

@keyframes slide-down-fade {
  0% {
    opacity: 0;
    transform: translateY(-2rem);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

</style>
